import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { SearchDatePicker } from "@/components/common/SearchDatePicker";
import { Plane, Clock, AlertCircle, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { searchFlights, AVAILABLE_CITIES, getAccessToken, isTokenExpired, getTokenExpiryTime } from "@/lib/amadeus";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const Flights = () => {
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [departureDate, setDepartureDate] = useState<Date | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [citySuggestions, setCitySuggestions] = useState<{ origin?: string; destination?: string } | null>(null);
  const [tokenExpiryTime, setTokenExpiryTime] = useState<number | null>(null);
  const { toast } = useToast();

  // Update token expiry countdown
  useEffect(() => {
    const interval = setInterval(() => {
      const expiryTime = getTokenExpiryTime();
      setTokenExpiryTime(expiryTime);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleRefreshToken = async () => {
    setIsRefreshingToken(true);
    try {
      await getAccessToken();
      toast({
        title: "Success",
        description: "Access token refreshed successfully",
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh access token",
        variant: "destructive",
      });
    } finally {
      setIsRefreshingToken(false);
    }
  };

  const handleSearch = async () => {
    if (!origin || !destination || !departureDate) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Debug log
    console.log('Search parameters:', {
      origin,
      destination,
      departureDate: departureDate.toISOString().split('T')[0]
    });

    // Check if token is expired
    if (isTokenExpired()) {
      toast({
        title: "Token expired",
        description: "Please refresh the access token before searching",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    setCitySuggestions(null);
    setSearchResults([]);

    try {
      // Save the search to Supabase with required fields
      await supabase.from('flight_searches').insert({
        origin,
        destination,
        departure_date: departureDate.toISOString().split('T')[0],
        passengers: 1,
        cabin_class: 'ECONOMY'
      });

      // Search flights using Amadeus API
      const results = await searchFlights(
        origin,
        destination,
        departureDate.toISOString().split('T')[0],
        undefined,  // returnDate is optional
        1,         // adults
        'ECONOMY'  // cabinClass
      );

      if (!results.data || results.data.length === 0) {
        setError("No flights found for this route and date. Please try different dates or cities.");
        return;
      }

      setSearchResults(results.data);
    } catch (error: any) {
      console.error("Error searching flights:", error);
      setError(error.message || "Failed to search for flights. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Format time remaining until token expiry
  const formatTimeRemaining = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <MainLayout>
      <div className="travel-container py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold">Search Flights</h1>
            <div className="flex items-center gap-4">
              {tokenExpiryTime !== null && (
                <span className="text-sm text-gray-600">
                  Token expires in: {formatTimeRemaining(tokenExpiryTime)}
                </span>
              )}
              <Button
                variant="outline"
                onClick={handleRefreshToken}
                disabled={isRefreshingToken}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {isRefreshingToken ? "Refreshing..." : "Refresh Token"}
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div>
              <label className="block text-sm font-medium mb-2">From</label>
              <Input
                placeholder="Enter origin city"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                list="originCities"
              />
              <datalist id="originCities">
                {AVAILABLE_CITIES.map(city => (
                  <option key={city} value={city} />
                ))}
              </datalist>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">To</label>
              <Input
                placeholder="Enter destination city"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                list="destinationCities"
              />
              <datalist id="destinationCities">
                {AVAILABLE_CITIES.map(city => (
                  <option key={city} value={city} />
                ))}
              </datalist>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Departure Date</label>
              <SearchDatePicker
                date={departureDate}
                onSelect={setDepartureDate}
                label="Select Date"
                minDate={new Date()}
              />
            </div>
            
            <div className="flex items-end">
              <Button
                className="w-full bg-travel-blue hover:bg-blue-700"
                onClick={handleSearch}
                disabled={isLoading || isTokenExpired()}
              >
                {isLoading ? "Searching..." : "Search Flights"}
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {searchResults.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">
                {searchResults.length} Flights Found
              </h2>
              
              {searchResults.map((flight: any, index: number) => (
                <Card key={index} className="w-full">
                  <CardHeader>
                    <CardTitle className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span>{flight.validatingAirlineCodes[0]}</span>
                        <Badge variant="outline">
                          {flight.numberOfBookableSeats} seats available
                        </Badge>
                      </div>
                      <span className="text-2xl font-bold">
                        ₹{parseFloat(flight.price.total).toLocaleString()}
                      </span>
                    </CardTitle>
                    <CardDescription>
                      {flight.itineraries[0].segments.map((segment: any, idx: number) => (
                        <span key={idx}>
                          {segment.carrierCode} {segment.number}
                          {idx < flight.itineraries[0].segments.length - 1 ? ' → ' : ''}
                        </span>
                      ))}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="grid grid-cols-2 gap-8">
                      <div>
                        <h3 className="font-medium mb-2">Departure</h3>
                        <p className="text-2xl font-bold">
                          {new Date(flight.itineraries[0].segments[0].departure.at).toLocaleTimeString()}
                        </p>
                        <p>{flight.itineraries[0].segments[0].departure.iataCode}</p>
                        <p className="text-sm text-gray-500">
                          Terminal {flight.itineraries[0].segments[0].departure.terminal || 'N/A'}
                        </p>
                      </div>
                      
                      <div>
                        <h3 className="font-medium mb-2">Arrival</h3>
                        <p className="text-2xl font-bold">
                          {new Date(flight.itineraries[0].segments[flight.itineraries[0].segments.length - 1].arrival.at).toLocaleTimeString()}
                        </p>
                        <p>{flight.itineraries[0].segments[flight.itineraries[0].segments.length - 1].arrival.iataCode}</p>
                        <p className="text-sm text-gray-500">
                          Terminal {flight.itineraries[0].segments[flight.itineraries[0].segments.length - 1].arrival.terminal || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                  
                  <CardFooter>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Duration: {flight.itineraries[0].duration}</span>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Flights;
